'use client'

import { FIELDNAMES } from '@/components/molecules/Form/utils/constants'
import ErrorModal from '@/components/molecules/Modal/ErrorModal'
import { usePayment, usePaymentModal } from '@/hooks'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import PaypalAdvancedCheckout from '../AdvancedCheckout'

const { DONATION_PRIVACY } = FIELDNAMES

const PaymentMethods = ({ name, isHidden = false, uiOptions = {} }) => {
  const [isDelayComplete, setIsDelayComplete] = useState(false)
  const { getValues, watch, setValue } = useFormContext()

  const searchParamsSource = useSearchParams()
  const searchParams = useMemo(
    () => Object.fromEntries(searchParamsSource.entries()),
    [searchParamsSource]
  )

  const {
    token,
    paymentOptions,
    paypalInitialOptions,
    paypalClientId,
    paymentState,
    setPaymentState,
    modalErrorIsOpen,
    setModalErrorIsOpen,
    isWindowActive,
    isProcessing,
    createPaymentOrder,
    handleOnCreateAlternativeOrder,
    handleOnApprove,
    handleOnError,
  } = usePayment({ paymentType: 'single', formData: getValues })

  const { modalConfig } = usePaymentModal({
    paymentState,
    setPaymentState,
    modalErrorIsOpen,
    setModalErrorIsOpen,
    isWindowActive,
    isProcessing,
  })

  const isPrivacyChecked = Boolean(watch(DONATION_PRIVACY))
  const excludedPaymentMethods = paymentOptions?.['disable-funding']
    ? [paymentOptions['disable-funding']]
    : []
  const isLoading =
    !token || !paypalClientId || !paymentOptions || !isDelayComplete

  const handlePayPalCreateOrder = useCallback(
    async (data) => {
      const { paymentSource } = data
      if (!paymentSource) return

      const response = await createPaymentOrder({ paymentSource, searchParams })
      if (response?.success && response?.orderID) {
        setValue(name, paymentSource)
        return response.orderID
      }
      throw new Error(response?.error?.message || 'Order creation failed')
    },
    [createPaymentOrder, searchParams, setValue, name]
  )

  const handleAlternativeFlow = useCallback(
    async (paymentSource) => {
      const response = await handleOnCreateAlternativeOrder(
        paymentSource,
        searchParams
      )
      if (response?.success) setValue(name, paymentSource)
      return response
    },
    [handleOnCreateAlternativeOrder, searchParams, setValue, name]
  )

  useEffect(() => {
    const timer = setTimeout(() => setIsDelayComplete(true), 1500)
    return () => clearTimeout(timer)
  }, [])

  if (isHidden) return null

  return (
    <div
      className='payment-methods'
      data-testid='payment-methods'
    >
      <ErrorModal {...modalConfig} />

      <PaypalAdvancedCheckout
        paypalInitialOptions={paypalInitialOptions}
        excludedPaymentMethods={excludedPaymentMethods}
        onCreateOrder={handlePayPalCreateOrder}
        onAlternativeCreateOrder={handleAlternativeFlow}
        onApprove={handleOnApprove}
        onError={handleOnError}
        isLoading={isLoading}
        isDisabled={!isPrivacyChecked}
        paymentState={paymentState}
        setPaymentState={setPaymentState}
        onSelectCreditCard={(method) => setValue(name, method)}
      />
    </div>
  )
}

export default PaymentMethods
